/**
 * Lightweight performance measurement utility using browser Performance API
 *
 * Provides core performance tracking functionality with:
 * - performance.mark() for timing specific points
 * - performance.measure() for measuring durations between marks
 * - Automatic console logging in development mode
 * - Callback subscription system for analytics integration
 * - Graceful degradation when Performance API is unavailable
 */

export type PerformanceMark = {
    name: string;
    startTime: number;
    duration: number;
    component: string;
};

class PerformanceMeasurements {
    private componentCallbacks = new Map<string, Set<(measurement: PerformanceMark) => void>>();
    private isEnabled: boolean;

    constructor() {
        this.isEnabled =
            typeof window !== "undefined" &&
            "performance" in window &&
            "mark" in window.performance &&
            "measure" in window.performance;
    }

    /**
     * Create a performance mark at a specific point in time
     */
    mark(name: string): void {
        if (!this.isEnabled) return;

        try {
            performance.mark(name);
        } catch (error) {
            // Silently fail in environments that don't support it
            console.debug("Performance mark failed:", error);
        }
    }

    /**
     * Measure the time between two marks
     */
    measure(component: string, name: string, startMark: string, endMark?: string): PerformanceMark | null {
        if (!this.isEnabled) return null;

        try {
            // Create end mark if not provided
            if (!endMark) {
                endMark = `${startMark}-end`;
                this.mark(endMark);
            }

            performance.measure(name, startMark, endMark);

            // Get the measurement
            const entries = performance.getEntriesByName(name, "measure");
            const latestEntry = entries[entries.length - 1];

            if (latestEntry) {
                const measurement: PerformanceMark = {
                    name: latestEntry.name,
                    startTime: latestEntry.startTime,
                    duration: latestEntry.duration,
                    component,
                };

                // Log in development (centralized to avoid duplicates)
                if (process.env.NODE_ENV === "development") {
                    console.debug(`⚡ Performance: ${measurement.name} took ${measurement.duration.toFixed(2)}ms`);
                }

                // Notify component-specific callbacks
                this.notifyComponentCallbacks(measurement);

                return measurement;
            }
        } catch (error) {
            console.debug("Performance measure failed:", (error as Error)?.message ?? error);
        }

        return null;
    }

    /**
     * Get the time since a specific mark
     */
    getTimeSinceMark(markName: string): number | null {
        if (!this.isEnabled) return null;

        try {
            const marks = performance.getEntriesByName(markName, "mark");
            if (marks.length > 0) {
                const mark = marks[marks.length - 1];
                return performance.now() - mark.startTime;
            }
        } catch (error) {
            console.debug("Get time since mark failed:", error);
        }

        return null;
    }

    /**
     * Get current high-resolution timestamp
     */
    now(): number {
        if (!this.isEnabled) return Date.now();
        return performance.now();
    }

    /**
     * Subscribe to measurement events for a specific component
     */
    onComponentMeasurement(component: string, callback: (measurement: PerformanceMark) => void): () => void {
        if (!this.componentCallbacks.has(component)) {
            this.componentCallbacks.set(component, new Set());
        }

        this.componentCallbacks.get(component)!.add(callback);

        // Return unsubscribe function
        return () => {
            const callbacks = this.componentCallbacks.get(component);
            if (callbacks) {
                callbacks.delete(callback);
                if (callbacks.size === 0) {
                    this.componentCallbacks.delete(component);
                }
            }
        };
    }

    /**
     * Notify component-specific callbacks when measurements match their component
     */
    private notifyComponentCallbacks(measurement: PerformanceMark): void {
        this.componentCallbacks.forEach((callbacks, component) => {
            // Check if measurement belongs to this component
            if (measurement.component === component) {
                callbacks.forEach((callback) => {
                    try {
                        callback(measurement);
                    } catch (error) {
                        console.error(`Performance callback error for component ${component}:`, error);
                    }
                });
            }
        });
    }

    /**
     * Get all performance marks
     */
    getMarks(): PerformanceEntry[] {
        if (!this.isEnabled) return [];
        return performance.getEntriesByType("mark");
    }

    /**
     * Get all performance measures
     */
    getMeasures(): PerformanceEntry[] {
        if (!this.isEnabled) return [];
        return performance.getEntriesByType("measure");
    }

    /**
     * Log all current measurements to console (development only)
     */
    logMeasurements(): void {
        if (process.env.NODE_ENV !== "development") return;

        const marks = this.getMarks();
        const measures = this.getMeasures();

        if (marks.length > 0 || measures.length > 0) {
            console.group("🚀 Performance Measurements");

            if (marks.length > 0) {
                console.debug(
                    "📍 Marks:",
                    marks.map((m) => `${m.name}: ${m.startTime.toFixed(2)}ms`),
                );
            }

            if (measures.length > 0) {
                console.debug(
                    "📏 Measures:",
                    measures.map((m) => `${m.name}: ${m.duration.toFixed(2)}ms`),
                );
            }

            console.groupEnd();
        }
    }
}

// Create singleton instance
export const perf = new PerformanceMeasurements();
